import { defineComponent as _defineComponent } from 'vue'
import { renderSlot as _renderSlot, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, unref as _unref, withCtx as _withCtx, createVNode as _createVNode, resolveComponent as _resolveComponent, resolveDirective as _resolveDirective, createElementBlock as _createElementBlock, withDirectives as _withDirectives, Fragment as _Fragment, normalizeClass as _normalizeClass, renderList as _renderList } from "vue"

const _hoisted_1 = {
  key: 0,
  class: "col"
}
const _hoisted_2 = { class: "d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2" }
const _hoisted_3 = { class: "me-sm-3 flex-grow-1" }
const _hoisted_4 = ["id", "title"]
const _hoisted_5 = { class: "my-2" }
const _hoisted_6 = { class: "mb-2 ms-sm-auto" }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { key: 3 }
const _hoisted_11 = { key: 4 }
const _hoisted_12 = {
  key: 0,
  class: "text-muted table-border mb-1"
}
const _hoisted_13 = { class: "d-flex flex-column flex-sm-row align-items-start justify-content-between gap-2" }
const _hoisted_14 = { class: "me-sm-3 flex-grow-1" }
const _hoisted_15 = ["id"]
const _hoisted_16 = ["id"]
const _hoisted_17 = ["for"]
const _hoisted_18 = { class: "route" }
const _hoisted_19 = { class: "visually-hidden" }
const _hoisted_20 = { class: "route__body" }
const _hoisted_21 = { class: "route__title mb-1" }
const _hoisted_22 = { key: 0 }
const _hoisted_23 = {
  key: 0,
  class: "table-border mb-1"
}
const _hoisted_24 = {
  key: 1,
  class: "table-border mb-1"
}

import {
  computed,
  ref,
  PropType,
  watch,
  watchEffect,
  onMounted,
  onBeforeUnmount,
} from 'vue';
import {
  ComputeTaskUtils,
  DataPipelineTaskTabOptionType,
  DATA_PIPELINE_TASK_TAB_OPTIONS,
} from '@/utils/compute-utils';
import {
  ComputeTaskCanonicalArtifactUrlDocument,
  ComputeTaskType,
} from '@/gql/graphql';
import apolloClient from '@/apollo/client';
import { ApolloError } from '@apollo/client/core';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BCollapse,
  BFormCheckbox,
  BFormGroup,
  BTable,
  BTooltip,
} from 'bootstrap-vue-next';
import { useUserStore } from '@/store/user';
import { IS_DEBUG_MODE, COMPUTE_TASK_STATUS } from '@/constants';
import type { TaskFileType } from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskFilesList from '@/components/compute/ComputeTaskFilesList.vue';
import ComputeTaskStatusBadge from '@/components/compute/ComputeTaskStatusBadge.vue';
import MermaidDiagram from '@/components/MermaidDiagram.vue';
import { buildProgressData } from '@/utils/nextflow-progress-data';
import {
  needsCanonicalProgress,
  shouldLoadCanonicalProgress,
  decodeCanonicalProgress,
} from '@/utils/nextflow-canonical-progress';


export default /*@__PURE__*/_defineComponent({
  __name: 'NextflowTask',
  props: {
  task: { type: Object as PropType<ComputeTaskType> },
  expanded: { type: Boolean, default: false },
  selectedTab: {
    type: String as PropType<DataPipelineTaskTabOptionType>,
    default: DATA_PIPELINE_TASK_TAB_OPTIONS.FILES,
  },
},
  emits: ["update:expanded", "update:selected-tab", "bucket-requested", "exec-details-requested", "debug-details-requested", "abort-requested", "directory-info-requested", "directory-view-requested", "file-preview-requested", "file-download-requested", "layout-needed"],
  setup(__props, { emit: __emit }) {

const userStore = useUserStore();

// --- Component Properties ---
const props = __props;

// --- Emits ---
const emits = __emit;

// helper to emit layout events with the task id
function emitLayout(phase: 'start' | 'end' | 'resize') {
  emits('layout-needed', phase, props.task?.id ?? null);
}

// Observe size changes (images/tables loading) and notify parent
const cardRef = ref<InstanceType<typeof BCard> | null>(null);
let resizeObserver: ResizeObserver | null = null;

onMounted(() => {
  const el = cardRef.value?.$el as HTMLElement | undefined;
  if (!el || typeof ResizeObserver === 'undefined') return;
  resizeObserver = new ResizeObserver(() => emitLayout('resize'));
  resizeObserver.observe(el);
});

onBeforeUnmount(() => {
  resizeObserver?.disconnect();
  requestVersion++;
});

// local reactive state ― decoupled from prop so the card can toggle itself
const selectedTab = ref(props.selectedTab);
const isExpanded = ref(props.expanded);
const detailedView = ref(true);
const exposeHiddenResults = ref(false);
const progress = computed(() => props.task?.results?.progressResults);
const archived = computed(() => needsCanonicalProgress(progress.value));
const archiveReady = computed(() =>
  shouldLoadCanonicalProgress(progress.value)
);
const archiveKey = computed(() =>
  props.task?.id
    ? `${props.task.id}:${
        progress.value?.canonicalPayloadArtifact?.sha256 || 'missing'
      }`
    : null
);
const archiveProcesses = ref<unknown[] | null>(null);
const loadedArchiveKey = ref<string | null>(null);
const loadingArchive = ref(false);
const archiveError = ref('');
const displayProcesses = computed(() =>
  archiveReady.value && archiveProcesses.value
    ? archiveProcesses.value
    : progress.value?.processes || []
);
const progressMappingOptions = computed(() => ({
  exposeHiddenResults: exposeHiddenResults.value,
  artifactLocation: {
    outputDir: props.task?.attributes?.stageOutput?.outputDir,
    bucketName: props.task?.bucket?.name,
  },
}));
const mappedArchiveProgress = computed(() =>
  buildProgressData(archiveProcesses.value || [], progressMappingOptions.value)
);
const mappedProgress = computed(() =>
  archiveReady.value && archiveProcesses.value
    ? mappedArchiveProgress.value
    : buildProgressData(displayProcesses.value, progressMappingOptions.value)
);
const mmdDiagram = computed(() => mappedProgress.value.mermaidDiagram ?? null);
let requestVersion = 0;
let pendingArchiveKey: string | null = null;

function archiveFailureText(error: unknown, taskId: number): string {
  const code =
    error instanceof ApolloError
      ? error.graphQLErrors.find((item) => item.extensions?.code)?.extensions
          ?.code
      : null;
  if (code === 'FORBIDDEN') {
    return 'You do not have access to these results. Ask a project owner for task access, then retry.';
  }
  if (code === 'NOT_FOUND')
    return 'This task was not found. Refresh the project page.';

  // Never render arbitrary exception text: a storage failure can contain a
  // signed URL. These are fixed messages created only by this component.
  const message = error instanceof Error ? error.message : '';
  if (message === 'archive-link-denied') {
    return 'The archive link expired or was denied. Retry to request a fresh link.';
  }
  if (message === 'archive-object-missing') {
    return `The archive file is missing. Retry, then contact support with task ${taskId} if it persists.`;
  }
  if (
    message === 'Archived results do not match this task.' ||
    message === 'Archived results are incomplete.' ||
    message === 'Archived result counts do not match this task.'
  ) {
    return `The archive failed verification. Retry, then contact support with task ${taskId} if it persists.`;
  }
  return `Archived results could not be loaded. Retry, then contact support with task ${taskId} if it persists.`;
}

async function loadArchive(force = false) {
  const id = props.task?.id;
  const key = archiveKey.value;
  const snapshot = progress.value;
  if (!isExpanded.value || !archiveReady.value) return;
  if (!id || !key || key.endsWith(':missing')) {
    archiveError.value =
      'The archive reference is missing. Refresh the project page, then contact support if it persists.';
    return;
  }
  if (!force && (loadedArchiveKey.value === key || pendingArchiveKey === key)) {
    return;
  }

  const version = ++requestVersion;
  pendingArchiveKey = key;
  loadingArchive.value = true;
  archiveError.value = '';
  try {
    const { data } = await apolloClient.query({
      query: ComputeTaskCanonicalArtifactUrlDocument,
      variables: { id },
      fetchPolicy: 'no-cache',
      context: { fetchOptions: { cache: 'no-store' } },
    });
    const url = data.computeTaskCanonicalArtifactUrl;
    if (!url) throw new Error('Archived results are unavailable.');
    const response = await fetch(url, {
      cache: 'no-store',
      credentials: 'omit',
      referrerPolicy: 'no-referrer',
    });
    if (!response.ok) {
      throw new Error(
        response.status === 401 || response.status === 403
          ? 'archive-link-denied'
          : response.status === 404
          ? 'archive-object-missing'
          : 'archive-fetch-failed'
      );
    }
    const processes = await decodeCanonicalProgress(
      snapshot,
      await response.arrayBuffer()
    );
    if (version !== requestVersion || archiveKey.value !== key) return;
    archiveProcesses.value = processes;
    loadedArchiveKey.value = key;
  } catch (error) {
    if (version !== requestVersion) return;
    archiveError.value = archiveFailureText(error, id);
  } finally {
    if (version === requestVersion) {
      pendingArchiveKey = null;
      loadingArchive.value = false;
    }
  }
}

watch(
  [isExpanded, archiveReady, archiveKey],
  ([expanded, needed, key], previous) => {
    if (key !== previous?.[2]) {
      requestVersion++;
      pendingArchiveKey = null;
      if (key?.split(':')[0] !== previous?.[2]?.split(':')[0]) {
        archiveProcesses.value = null;
        loadedArchiveKey.value = null;
      }
      loadingArchive.value = false;
      archiveError.value = '';
    }
    if (expanded && needed) void loadArchive();
  },
  { immediate: true }
);

watchEffect(
  () => (
    (isExpanded.value = props.expanded),
    console.debug('isExpanded set to', isExpanded.value, 'from prop')
  )
);
watchEffect(
  () => (
    (selectedTab.value = props.selectedTab),
    console.debug('selectedTab set to', selectedTab.value, 'from prop')
  )
);

function selectTab(tab: DataPipelineTaskTabOptionType) {
  selectedTab.value = tab;
  emits('update:selected-tab', selectedTab.value);
}

function toggleCard() {
  isExpanded.value = !isExpanded.value;
  emits('update:expanded', isExpanded.value);
}

function expandCard() {
  if (!isExpanded.value) {
    toggleCard();
  }
}

const progressFields = [
  { key: 'label', label: 'Item' },
  { key: 'value', label: 'Value', tdClass: 'text-end font-monospace' },
];

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!
  const _directive_b_tooltip = _resolveDirective("b-tooltip")!

  return (props.task?.id)
    ? (_openBlock(), _createElementBlock("div", _hoisted_1, [
        _createVNode(_unref(BCard), {
          ref_key: "cardRef",
          ref: cardRef,
          class: _normalizeClass([
        'd-flex flex-column p-0 card-sample',
        isExpanded.value ? 'expanded' : 'collapsed h-100',
      ])
        }, {
          default: _withCtx(() => [
            _renderSlot(_ctx.$slots, "default", {}, () => [
              _createElementVNode("div", _hoisted_2, [
                _createElementVNode("div", _hoisted_3, [
                  _withDirectives((_openBlock(), _createElementBlock("h2", {
                    onClick: toggleCard,
                    id: `task-title-${props.task.id}`,
                    class: "mb-0 task-title",
                    title: isExpanded.value ? 'Click to collapse' : 'Click to expand'
                  }, [
                    (!isExpanded.value)
                      ? (_openBlock(), _createBlock(ComputeTaskStatusBadge, {
                          key: 0,
                          task: props.task,
                          "icon-only": ""
                        }, null, 8, ["task"]))
                      : _createCommentVNode("", true),
                    _createTextVNode(" " + _toDisplayString(props.task.name) + " ", 1),
                    _createVNode(_unref(BBadge), {
                      variant: "warning",
                      class: "ms-2"
                    }, {
                      default: _withCtx(() => [...(_cache[26] || (_cache[26] = [
                        _createElementVNode("small", null, [
                          _createElementVNode("i", { class: "fab fa-neos" }),
                          _createTextVNode(" Nextflow ")
                        ], -1)
                      ]))]),
                      _: 1
                    }),
                    (isExpanded.value)
                      ? (_openBlock(), _createBlock(_component_font_awesome_icon, {
                          key: 1,
                          icon: "chevron-up",
                          class: "ms-2"
                        }))
                      : _createCommentVNode("", true)
                  ], 8, _hoisted_4)), [
                    [
                      _directive_b_tooltip,
                      void 0,
                      void 0,
                      { hover: true }
                    ]
                  ]),
                  _createElementVNode("div", _hoisted_5, [
                    _createVNode(ComputeTaskStatusBadge, {
                      task: props.task
                    }, null, 8, ["task"])
                  ])
                ]),
                _createElementVNode("div", _hoisted_6, [
                  (!isExpanded.value)
                    ? (_openBlock(), _createBlock(_unref(BButton), {
                        key: 0,
                        onClick: expandCard,
                        variant: "link",
                        size: "sm",
                        class: "p-0 m-0 link-secondary text-decoration-none"
                      }, {
                        default: _withCtx(() => [
                          _cache[27] || (_cache[27] = _createTextVNode(" Show More ", -1)),
                          _createVNode(_component_font_awesome_icon, {
                            icon: "chevron-down",
                            class: "ms-1"
                          })
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (isExpanded.value)
                    ? (_openBlock(), _createBlock(_unref(BButtonGroup), { key: 1 }, {
                        default: _withCtx(() => [
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 0,
                                hidden: !mmdDiagram.value,
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[0] || (_cache[0] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).DIAGRAM);
                }),
                                id: `btn-diagram-${props.task.id}`,
                                title: "View diagram",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).DIAGRAM
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "diagram-project" })
                                ]),
                                _: 1
                              }, 8, ["hidden", "id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 1,
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[1] || (_cache[1] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS);
                }),
                                id: `btn-progress-${props.task.id}`,
                                title: "View progress timeline with stats",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "list-check" })
                                ]),
                                _: 1
                              }, 8, ["id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 2,
                                hidden: "",
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[2] || (_cache[2] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES);
                }),
                                id: `btn-figures-${props.task.id}`,
                                title: "View figures",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FIGURES
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "chart-line" })
                                ]),
                                _: 1
                              }, 8, ["id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (props.task.bucket)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 3,
                                size: "sm",
                                variant: "outline-dark",
                                onClick: _cache[3] || (_cache[3] = ($event: any) => {
                  expandCard();
                  selectTab(_unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES);
                }),
                                id: `btn-files-${props.task.id}`,
                                title: "View files",
                                active: 
                  isExpanded.value &&
                  selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES
                
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: "file" })
                                ]),
                                _: 1
                              }, 8, ["id", "active"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                            variant: "outline-dark",
                            size: "sm",
                            onClick: _cache[4] || (_cache[4] = ($event: any) => (emits('exec-details-requested'))),
                            id: `btn-exec-${props.task.id}`,
                            title: "View exec configuration"
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_font_awesome_icon, { icon: ['fas', 'sliders'] })
                            ]),
                            _: 1
                          }, 8, ["id"])), [
                            [
                              _directive_b_tooltip,
                              void 0,
                              void 0,
                              { hover: true }
                            ]
                          ]),
                          (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 4,
                                variant: "outline-dark",
                                size: "sm",
                                onClick: _cache[5] || (_cache[5] = ($event: any) => (emits('bucket-requested'))),
                                id: `btn-bucket-${props.task.id}`,
                                disabled: !props.task.bucket,
                                title: "Go to data"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: ['fas', 'database'] })
                                ]),
                                _: 1
                              }, 8, ["id", "disabled"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          (_unref(IS_DEBUG_MODE) || _unref(userStore).developer)
                            ? _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                                key: 5,
                                variant: "outline-dark",
                                size: "sm",
                                onClick: _cache[6] || (_cache[6] = ($event: any) => (emits('debug-details-requested'))),
                                id: `btn-debug-${props.task.id}`,
                                title: "View debug details"
                              }, {
                                default: _withCtx(() => [
                                  _createVNode(_component_font_awesome_icon, { icon: ['fas', 'code'] })
                                ]),
                                _: 1
                              }, 8, ["id"])), [
                                [
                                  _directive_b_tooltip,
                                  void 0,
                                  void 0,
                                  { hover: true }
                                ]
                              ])
                            : _createCommentVNode("", true),
                          _withDirectives((_openBlock(), _createBlock(_unref(BButton), {
                            size: "sm",
                            variant: "outline-danger",
                            disabled: !_unref(ComputeTaskUtils).canBeAborted(props.task.status),
                            onClick: _cache[7] || (_cache[7] = ($event: any) => (emits('abort-requested'))),
                            id: `btn-abort-${props.task.id}`,
                            title: "Abort task",
                            active: props.task.status === _unref(COMPUTE_TASK_STATUS).ABORTING
                          }, {
                            default: _withCtx(() => [
                              _createVNode(_component_font_awesome_icon, { icon: "times" })
                            ]),
                            _: 1
                          }, 8, ["disabled", "id", "active"])), [
                            [
                              _directive_b_tooltip,
                              void 0,
                              void 0,
                              { hover: true }
                            ]
                          ])
                        ]),
                        _: 1
                      }))
                    : _createCommentVNode("", true)
                ])
              ]),
              _createVNode(_unref(BCollapse), {
                modelValue: isExpanded.value,
                "onUpdate:modelValue": _cache[21] || (_cache[21] = ($event: any) => ((isExpanded).value = $event)),
                class: "mt-2 flex-grow-1",
                onShow: _cache[22] || (_cache[22] = ($event: any) => (emitLayout('start'))),
                onHide: _cache[23] || (_cache[23] = ($event: any) => (emitLayout('start'))),
                onShown: _cache[24] || (_cache[24] = ($event: any) => (emitLayout('end'))),
                onHidden: _cache[25] || (_cache[25] = ($event: any) => (emitLayout('end')))
              }, {
                default: _withCtx(() => [
                  (mappedProgress.value.artifactPathError)
                    ? (_openBlock(), _createBlock(_unref(BAlert), {
                        key: 0,
                        "model-value": true,
                        variant: "warning"
                      }, {
                        default: _withCtx(() => [...(_cache[28] || (_cache[28] = [
                          _createTextVNode(" Some result files have an invalid storage location. Refresh the project page, then contact support if it persists. ", -1)
                        ]))]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (
              archiveError.value &&
              archiveProcesses.value &&
              loadedArchiveKey.value !== archiveKey.value
            )
                    ? (_openBlock(), _createBlock(_unref(BAlert), {
                        key: 1,
                        "model-value": true,
                        variant: "info"
                      }, {
                        default: _withCtx(() => [...(_cache[29] || (_cache[29] = [
                          _createTextVNode("Showing the previously verified results while the latest archive is unavailable.", -1)
                        ]))]),
                        _: 1
                      }))
                    : _createCommentVNode("", true),
                  (archived.value)
                    ? (_openBlock(), _createBlock(_unref(BAlert), {
                        key: 2,
                        "model-value": true,
                        variant: archiveError.value ? 'danger' : 'info'
                      }, {
                        default: _withCtx(() => [
                          (loadingArchive.value)
                            ? (_openBlock(), _createElementBlock("span", _hoisted_7, "Loading archived results..."))
                            : (archiveError.value)
                              ? (_openBlock(), _createElementBlock("span", _hoisted_8, _toDisplayString(archiveError.value), 1))
                              : (loadedArchiveKey.value === archiveKey.value)
                                ? (_openBlock(), _createElementBlock("span", _hoisted_9, "Archived results loaded."))
                                : (!archiveReady.value)
                                  ? (_openBlock(), _createElementBlock("span", _hoisted_10, "Inline results will keep updating; archived results load when collection finishes."))
                                  : (_openBlock(), _createElementBlock("span", _hoisted_11, "Results are stored in an archive.")),
                          (archiveError.value)
                            ? (_openBlock(), _createBlock(_unref(BButton), {
                                key: 5,
                                size: "sm",
                                variant: "outline-dark",
                                class: "ms-2",
                                onClick: _cache[8] || (_cache[8] = ($event: any) => (loadArchive(true)))
                              }, {
                                default: _withCtx(() => [...(_cache[30] || (_cache[30] = [
                                  _createTextVNode("Retry", -1)
                                ]))]),
                                _: 1
                              }))
                            : _createCommentVNode("", true)
                        ]),
                        _: 1
                      }, 8, ["variant"]))
                    : _createCommentVNode("", true),
                  (selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).DIAGRAM)
                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 3 }, [
                        (!mmdDiagram.value)
                          ? (_openBlock(), _createElementBlock("div", _hoisted_12, " No diagram available for this task. "))
                          : (_openBlock(), _createBlock(MermaidDiagram, {
                              key: 1,
                              source: mmdDiagram.value,
                              "diagram-id": props.task?.id,
                              onRendered: _cache[9] || (_cache[9] = ($event: any) => (emitLayout('end')))
                            }, null, 8, ["source", "diagram-id"]))
                      ], 64))
                    : (displayProcesses.value.length || archived.value)
                      ? (_openBlock(), _createElementBlock(_Fragment, { key: 4 }, [
                          _createElementVNode("div", _hoisted_13, [
                            _createElementVNode("div", _hoisted_14, [
                              _createElementVNode("h3", {
                                onClick: _cache[10] || (_cache[10] = ($event: any) => (detailedView.value = !detailedView.value)),
                                style: {"cursor":"pointer"},
                                id: `progress-title-${props.task.id}`
                              }, [
                                (
                      selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS
                    )
                                  ? (_openBlock(), _createElementBlock(_Fragment, { key: 0 }, [
                                      _createTextVNode(" Progress ")
                                    ], 64))
                                  : (
                      selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES
                    )
                                    ? (_openBlock(), _createElementBlock(_Fragment, { key: 1 }, [
                                        _createTextVNode(" Files ")
                                      ], 64))
                                    : _createCommentVNode("", true),
                                _createElementVNode("i", {
                                  class: _normalizeClass(
                      detailedView.value
                        ? ['ms-1', 'bi', 'bi-arrows-collapse']
                        : ['ms-1', 'bi', 'bi-arrows-expand']
                    )
                                }, null, 2)
                              ], 8, _hoisted_15),
                              _createVNode(_unref(BTooltip), {
                                target: `progress-title-${props.task.id}`,
                                placement: "top-start",
                                triggers: "hover"
                              }, {
                                default: _withCtx(() => [
                                  _createTextVNode(_toDisplayString(detailedView.value ? 'Click to collapse' : 'Click to expand'), 1)
                                ]),
                                _: 1
                              }, 8, ["target"])
                            ]),
                            _createElementVNode("div", {
                              class: "ms-sm-auto",
                              id: `form-group-toggle-hidden-results-${props.task.id}`
                            }, [
                              _createVNode(_unref(BFormGroup), { class: "d-flex flex-nowrap align-items-center" }, {
                                default: _withCtx(() => [
                                  _createElementVNode("label", {
                                    for: `toggle-hidden-results-${props.task.id}`,
                                    class: "small"
                                  }, "Show more", 8, _hoisted_17),
                                  _createVNode(_unref(BFormCheckbox), {
                                    modelValue: exposeHiddenResults.value,
                                    "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((exposeHiddenResults).value = $event)),
                                    size: "sm",
                                    class: "ms-1",
                                    id: `toggle-hidden-results-${props.task.id}`,
                                    switch: ""
                                  }, null, 8, ["modelValue", "id"])
                                ]),
                                _: 1
                              })
                            ], 8, _hoisted_16),
                            _createVNode(_unref(BTooltip), {
                              target: `form-group-toggle-hidden-results-${props.task.id}`,
                              triggers: "hover",
                              placement: "left"
                            }, {
                              default: _withCtx(() => [...(_cache[31] || (_cache[31] = [
                                _createTextVNode(" Expose hidden results ", -1)
                              ]))]),
                              _: 1
                            }, 8, ["target"])
                          ]),
                          _createElementVNode("div", _hoisted_18, [
                            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(mappedProgress.value.processes, (proc, i) => {
                              return (_openBlock(), _createElementBlock("div", {
                                key: i,
                                class: "route__section"
                              }, [
                                _createElementVNode("div", {
                                  class: _normalizeClass(["route__marker", proc.state])
                                }, [
                                  _createElementVNode("span", _hoisted_19, "ProgressSection " + _toDisplayString(i + 1), 1)
                                ], 2),
                                _createElementVNode("div", _hoisted_20, [
                                  _createElementVNode("h5", _hoisted_21, [
                                    (proc.sampleId)
                                      ? (_openBlock(), _createElementBlock("span", _hoisted_22, _toDisplayString(proc.sampleId) + ": ", 1))
                                      : _createCommentVNode("", true),
                                    _createTextVNode(" " + _toDisplayString(proc.name), 1)
                                  ]),
                                  _createVNode(_unref(BCollapse), {
                                    modelValue: detailedView.value,
                                    "onUpdate:modelValue": _cache[16] || (_cache[16] = ($event: any) => ((detailedView).value = $event)),
                                    onShow: _cache[17] || (_cache[17] = ($event: any) => (emitLayout('start'))),
                                    onHide: _cache[18] || (_cache[18] = ($event: any) => (emitLayout('start'))),
                                    onShown: _cache[19] || (_cache[19] = ($event: any) => (emitLayout('end'))),
                                    onHidden: _cache[20] || (_cache[20] = ($event: any) => (emitLayout('end')))
                                  }, {
                                    default: _withCtx(() => [
                                      (
                        selectedTab.value ===
                          _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).PROGRESS &&
                        proc.results?.length
                      )
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_23, [
                                            _createVNode(_unref(BTable), {
                                              items: proc.results,
                                              fields: progressFields,
                                              small: "",
                                              striped: "",
                                              hover: "",
                                              responsive: "sm",
                                              stacked: "sm",
                                              class: "browser-table route__table mb-0",
                                              "thead-class": "visually-hidden"
                                            }, {
                                              "cell(label)": _withCtx(({ item }) => [
                                                _createTextVNode(_toDisplayString(item.label), 1)
                                              ]),
                                              "cell(value)": _withCtx(({ item }) => [
                                                _createTextVNode(_toDisplayString(item.value), 1)
                                              ]),
                                              _: 1
                                            }, 8, ["items"])
                                          ]))
                                        : _createCommentVNode("", true),
                                      (
                        selectedTab.value === _unref(DATA_PIPELINE_TASK_TAB_OPTIONS).FILES &&
                        proc.files?.length
                      )
                                        ? (_openBlock(), _createElementBlock("div", _hoisted_24, [
                                            _createVNode(ComputeTaskFilesList, {
                                              files: proc.files,
                                              onDirectoryInfoRequested: _cache[12] || (_cache[12] = ($event: any) => (
                          emits('directory-info-requested', $event)
                        )),
                                              onDirectoryViewRequested: _cache[13] || (_cache[13] = ($event: any) => (
                          emits('directory-view-requested', $event)
                        )),
                                              onFilePreviewRequested: _cache[14] || (_cache[14] = ($event: any) => (
                          emits('file-preview-requested', $event)
                        )),
                                              onFileDownloadRequested: _cache[15] || (_cache[15] = ($event: any) => (
                          emits('file-download-requested', $event)
                        ))
                                            }, null, 8, ["files"])
                                          ]))
                                        : _createCommentVNode("", true)
                                    ]),
                                    _: 2
                                  }, 1032, ["modelValue"])
                                ])
                              ]))
                            }), 128))
                          ])
                        ], 64))
                      : _createCommentVNode("", true)
                ]),
                _: 1
              }, 8, ["modelValue"])
            ])
          ]),
          _: 3
        }, 8, ["class"])
      ]))
    : _createCommentVNode("", true)
}
}

})